import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Plus, Minus } from 'lucide-react';

const FAQ_ITEMS = [
  {
    q: 'How much does Real Leads CRM cost?',
    a: 'Per-seat pricing that scales with you: $35/seat for solo, $30/seat for teams of 2–5, and just $25/seat for 6+. Annual billing saves another ~17%. Every plan includes unlimited leads, the full Kanban pipeline, AI voice notes, email tracking, and team collaboration.',
  },
  {
    q: 'What is shared lead management?',
    a: 'Shared lead management means your whole team can collaborate on the same opportunity instead of working in siloed pipelines. Real Leads lets you share specific leads with teammates, assign them automatically, and track activity across the team in one shared view.',
  },
  {
    q: 'Can I import leads from another CRM?',
    a: 'Yes. Real Leads supports CSV import, direct CRM import (HubSpot, Salesforce, Pipedrive), and one-click sourcing from Seamless.AI with 17+ data fields auto-mapped — title, department, industry, SIC/NAICS codes, mobile number, and more.',
  },
  {
    q: 'Does Real Leads CRM have a mobile app?',
    a: 'Real Leads is a Progressive Web App (PWA). You can install it directly from your browser on iOS, Android, Mac, or Windows. It works offline, queues voice notes when you lose signal, and sends push notifications for lead shares and reminders.',
  },
  {
    q: 'How do AI voice notes work?',
    a: 'Tap the microphone on any lead, dictate your update, and Real Leads transcribes it with OpenAI Whisper, then uses Claude Sonnet 4.5 to extract a summary plus action items with due dates. You can convert any action item into a draft calendar appointment with one click.',
  },
  {
    q: 'Is my data secure?',
    a: 'All data is encrypted in transit (TLS 1.3) and at rest. Role-based access control (Super Admin / Admin / Member / Viewer) restricts who can see what, and every action is logged in an activity audit trail. Member visibility is restricted by default.',
  },
  {
    q: 'Do you offer a free trial?',
    a: 'Yes — sign up takes 60 seconds, no credit card required. You\'ll get full access to every feature so you can try lead sharing, voice notes, the Kanban pipeline, and team performance reports before committing.',
  },
  {
    q: 'What integrations does Real Leads support?',
    a: 'Out of the box: Stripe & PayPal for billing, Resend for email digests, Seamless.AI for lead sourcing, OpenAI Whisper + Claude for AI features, Web Push for notifications, and webhooks for custom integrations. More on the roadmap.',
  },
];

export function FaqSection() {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section className="py-24 sm:py-32 border-t border-white/10" data-testid="faq-section">
      {/* JSON-LD FAQPage schema — drives "People also ask" rich results in Google */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: FAQ_ITEMS.map(item => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
              },
            })),
          })}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">FAQ</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Questions, answered.
          </h2>
          <p className="text-base text-zinc-400">Everything you need to know before you sign up.</p>
        </div>

        <div className="space-y-3">
          {FAQ_ITEMS.map((item, idx) => {
            const open = openIdx === idx;
            return (
              <div
                key={item.q}
                className="border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] rounded-lg overflow-hidden transition-colors"
                data-testid={`faq-item-${idx}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : idx)}
                  className="w-full text-left px-5 py-4 flex items-center justify-between gap-4"
                  aria-expanded={open}
                  data-testid={`faq-toggle-${idx}`}
                >
                  <span className="text-base font-medium text-white">{item.q}</span>
                  <span className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center shrink-0">
                    {open ? <Minus className="w-3.5 h-3.5 text-zinc-400" /> : <Plus className="w-3.5 h-3.5 text-zinc-400" />}
                  </span>
                </button>
                {open && (
                  <div className="px-5 pb-5 text-sm text-zinc-400 leading-relaxed" data-testid={`faq-answer-${idx}`}>
                    {item.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
